import type {
  IdeaCardBodyMode,
  IdeaCardBodySection,
  IdeaCardLayout,
  IdeaCardPreviewData,
  IdeaCardScoreUnit,
  MarkdownIdeaCardInputData,
  NormalizedIdeaCardBodySection,
  NormalizedMarkdownIdeaCardData,
  NormalizedSectionedHorizontalIdeaCardData,
  SectionedHorizontalIdeaCardInputData,
  ScoreRange,
} from '@/lib/idea-card-types';

interface IdeaCardFactoryBase {
  title: string;
  score?: number;
  scoreRange?: ScoreRange;
  scoreUnit?: IdeaCardScoreUnit;
  scoreLabel?: string;
  layout?: IdeaCardLayout;
  bodyMode?: IdeaCardBodyMode;
  preview?: IdeaCardPreviewData;
  groupLabel?: string;
  infoTooltip?: string;
}

interface NormalizedIdeaCardFactoryBase extends IdeaCardFactoryBase {
  displayTitle: string;
  displayBody?: string;
  displayUrl?: string;
}

export const createMarkdownCard = ({
  title,
  body,
  score,
  scoreRange,
  scoreUnit,
  scoreLabel,
  layout,
  bodyMode,
  preview,
  groupLabel,
  infoTooltip,
}: IdeaCardFactoryBase & { body?: string }): MarkdownIdeaCardInputData => ({
  title,
  bodyModel: 'markdown',
  body,
  score,
  scoreRange,
  scoreUnit,
  scoreLabel,
  layout,
  bodyMode,
  preview,
  groupLabel,
  infoTooltip,
});

export const createSectionedHorizontalCard = ({
  title,
  bodySections,
  score,
  scoreRange,
  scoreUnit,
  scoreLabel,
  bodyMode,
  preview,
  groupLabel,
  infoTooltip,
}: IdeaCardFactoryBase & { bodySections: IdeaCardBodySection[] }): SectionedHorizontalIdeaCardInputData => ({
  title,
  layout: 'horizontal',
  bodyModel: 'sectioned_horizontal',
  bodySections,
  score,
  scoreRange,
  scoreUnit,
  scoreLabel,
  bodyMode,
  preview,
  groupLabel,
  infoTooltip,
});

export const createNormalizedMarkdownCard = ({
  title,
  body,
  score,
  scoreUnit,
  scoreLabel,
  layout,
  bodyMode,
  preview,
  groupLabel,
  displayTitle,
  displayBody,
  displayUrl,
}: NormalizedIdeaCardFactoryBase & { body?: string }): NormalizedMarkdownIdeaCardData => ({
  title,
  bodyModel: 'markdown',
  body,
  score,
  scoreUnit,
  scoreLabel,
  layout,
  bodyMode,
  preview,
  groupLabel,
  displayTitle,
  displayBody,
  displayUrl,
});

export const createNormalizedSectionedHorizontalCard = ({
  title,
  bodySections,
  displayBodySections,
  score,
  scoreUnit,
  scoreLabel,
  bodyMode,
  preview,
  groupLabel,
  displayTitle,
  displayBody,
  displayUrl,
}: NormalizedIdeaCardFactoryBase & {
  bodySections: IdeaCardBodySection[];
  displayBodySections: NormalizedIdeaCardBodySection[];
}): NormalizedSectionedHorizontalIdeaCardData => ({
  title,
  layout: 'horizontal',
  bodyModel: 'sectioned_horizontal',
  bodySections,
  displayBodySections,
  score,
  scoreUnit,
  scoreLabel,
  bodyMode,
  preview,
  groupLabel,
  displayTitle,
  displayBody,
  displayUrl,
});
